import { msalInstance, silentRedirectUri } from '../msalInstance';
import { apiTokenRequest } from '../authConfig';
import { requiresInteractiveConsent, triggerConsentRedirect } from '../utils/apiAuthHelper';

let cachedAccessToken: string | null = null;
let tokenExpiresAt: number = 0; // ms timestamp
let pendingTokenPromise: Promise<string | null> | null = null;

/**
 * Get or refresh the Azure AD Bearer access token for backend API calls.
 * Caches token in memory until 5 minutes before expiry to avoid redundant
 * acquireTokenSilent calls and MSAL mutex contention on every HTTP request.
 */
export async function getCachedApiAccessToken(forceRefresh = false): Promise<string | null> {
  if (!apiTokenRequest) return null;

  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) return null;

  const now = Date.now();
  // Return cached token if still valid (buffer: 5 minutes)
  if (!forceRefresh && cachedAccessToken && (tokenExpiresAt - now > 5 * 60 * 1000)) {
    return cachedAccessToken;
  }

  // Deduplicate concurrent token acquisitions so parallel requests share one call
  if (pendingTokenPromise && !forceRefresh) {
    return pendingTokenPromise;
  }

  pendingTokenPromise = (async () => {
    try {
      const response = await msalInstance.acquireTokenSilent({
        ...apiTokenRequest,
        account: accounts[0],
        forceRefresh,
        redirectUri: silentRedirectUri,
      });

      cachedAccessToken = response.accessToken;
      tokenExpiresAt = response.expiresOn ? response.expiresOn.getTime() : (Date.now() + 55 * 60 * 1000);
      return cachedAccessToken;
    } catch (error) {
      cachedAccessToken = null;
      tokenExpiresAt = 0;
      if (requiresInteractiveConsent(error)) {
        await triggerConsentRedirect();
      } else {
        console.error('[Auth] Failed to acquire API token:', error instanceof Error ? error.message : error);
      }
      return null;
    } finally {
      pendingTokenPromise = null;
    }
  })();

  return pendingTokenPromise;
}

export function clearCachedApiAccessToken(): void {
  cachedAccessToken = null;
  tokenExpiresAt = 0;
  pendingTokenPromise = null;
}

/**
 * Get Authorization headers for fetch-based API calls.
 * Returns empty object when API auth is not configured.
 */
export async function getAuthHeaders(): Promise<Record<string, string>> {
  const token = await getCachedApiAccessToken();
  if (!token) return {};
  return { Authorization: `Bearer ${token}` };
}
